<script lang="ts" setup>
import type { ImageEditorExportFormat } from '../../runtime/imageEditor';
import type {
    CropSelection,
    CropperHistoryState,
    WatermarkPositionPreset,
    WatermarkType,
    WebsiteLogoOption
} from '../../runtime/cropper';
import CropperRange from './CropperRange.vue';

defineProps<{
    cropSelection: CropSelection;
    websiteLogoOptions: WebsiteLogoOption[];
    websiteLogoLoading: boolean;
    watermarkPositionPresets: WatermarkPositionPreset[];
    exportFormats: { value: ImageEditorExportFormat; text: string }[];
    isLocalFileEditor: boolean;
    exportQualityDisabled: boolean;
    dialog: boolean;
    embedded?: boolean;
    saving: boolean;
    backgroundRemovalLoading: boolean;
    historyIndex: number;
    history: CropperHistoryState[];
    isWatermarkPositionPresetActive: (preset: WatermarkPositionPreset) => boolean;
}>();

const emit = defineEmits<{
    (e: 'save-history', label: string): void;
    (e: 'set-watermark-type'): void;
    (e: 'website-logo-change'): void;
    (e: 'enable-watermark'): void;
    (e: 'watermark-logo', event: Event): void;
    (e: 'set-watermark-position', x: number, y: number): void;
    (e: 'update-watermark-control'): void;
    (e: 'open-dialog'): void;
    (e: 'remove-background'): void;
    (e: 'save-edited-image'): void;
    (e: 'reset-editor'): void;
    (e: 'undo'): void;
    (e: 'redo'): void;
}>();

const { $t } = useLocalization();
const fieldId = useId();

const watermarkEnabled = defineModel<boolean>('watermarkEnabled', { required: true });
const watermarkType = defineModel<WatermarkType>('watermarkType', { required: true });
const watermarkText = defineModel<string>('watermarkText', { required: true });
const selectedWebsiteLogoId = defineModel<number | null>('selectedWebsiteLogoId', { required: true });
const watermarkX = defineModel<number>('watermarkX', { required: true });
const watermarkY = defineModel<number>('watermarkY', { required: true });
const watermarkOpacity = defineModel<number>('watermarkOpacity', { required: true });
const watermarkSize = defineModel<number>('watermarkSize', { required: true });
const exportFormat = defineModel<ImageEditorExportFormat>('exportFormat', { required: true });
const outputFileName = defineModel<string>('outputFileName', { required: true });
const quality = defineModel<number>('quality', { required: true });
const outputResizeEnabled = defineModel<boolean>('outputResizeEnabled', { required: true });
const outputWidth = defineModel<number>('outputWidth', { required: true });
const outputHeight = defineModel<number>('outputHeight', { required: true });
const optimizeDimensions = defineModel<boolean>('optimizeDimensions', { required: true });
const previewCanvas = ref<HTMLCanvasElement | null>(null);
const watermarkLogoInput = ref<HTMLInputElement | null>(null);

defineExpose({
    previewCanvas,
    watermarkLogoInput
});
</script>

<template>
    <div class="flex min-w-0 flex-col gap-[16px]">
        <EditoPageSurface title="[[[Podgląd]]]">
            <div class="grid min-h-[140px] place-items-center rounded-[8px] border border-border bg-surface-2 p-[12px] dark:border-border-dark dark:bg-surface-2-dark">
                <canvas ref="previewCanvas" class="max-h-[140px] max-w-full bg-surface" />
            </div>
            <div class="mt-[8px] text-center text-ui-body text-fg-muted dark:text-fg-muted-dark">
                <template v-if="cropSelection.active">
                    x: {{ cropSelection.x }}px · y: {{ cropSelection.y }}px<br>
                    {{ cropSelection.width }}×{{ cropSelection.height }}px
                </template>
                <template v-else>
                    {{ $t('[[[Cały obraz]]]') }}
                </template>
                <br>
                {{ $t('[[[Wynik]]]') }}: {{ previewCanvas?.width || 0 }}×{{ previewCanvas?.height || 0 }}px
            </div>
        </EditoPageSurface>

        <EditoPageSurface title="[[[Znak wodny]]]">
            <IdeoFormCheckbox v-model="watermarkEnabled" class="editor-watermark-toggle" @change="emit('save-history', $t('[[[Zmiana znaku wodnego]]]'))">
                {{ $t('[[[Znak wodny]]]') }}
            </IdeoFormCheckbox>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="$t('[[[Typ znaku wodnego]]]')" :label-for="fieldId + '-watermarkType'"><IdeoFormSelect :id="fieldId + '-watermarkType'" v-model="watermarkType" :aria-label="$t('[[[Typ znaku wodnego]]]')" @change="emit('set-watermark-type')">
                <IdeoFormSelectOption value="text">
                    {{ $t('[[[Tekst]]]') }}
                </IdeoFormSelectOption>
                <IdeoFormSelectOption value="logo">
                    {{ $t('[[[Logo]]]') }}
                </IdeoFormSelectOption>
            </IdeoFormSelect></IdeoFormGroup>
            <IdeoFormGroup v-if="watermarkType === 'text'" nospace class="mt-[12px] first:mt-0" :label="$t('[[[Tekst znaku wodnego]]]')" :label-for="fieldId + '-watermarkText'"><IdeoFormInput :id="fieldId + '-watermarkText'" v-model="watermarkText" :aria-label="$t('[[[Tekst znaku wodnego]]]')" @change="emit('update-watermark-control')" /></IdeoFormGroup>
            <div v-else class="mt-[8px] grid grid-cols-1 gap-[8px]">
                <IdeoFormGroup v-if="websiteLogoOptions.length > 0 || websiteLogoLoading" nospace class="mt-[12px] first:mt-0" :label="$t('[[[Logo witryny]]]')" :label-for="fieldId + '-selectedWebsiteLogoId'"><IdeoFormSelect :id="fieldId + '-selectedWebsiteLogoId'"
                    v-model="selectedWebsiteLogoId" :aria-label="$t('[[[Logo witryny]]]')"
                    :disabled="websiteLogoLoading"
                    @change="emit('website-logo-change')"
                >
                    <IdeoFormSelectOption v-if="websiteLogoLoading" :value="null">
                        {{ $t('[[[Ładowanie logo witryn...]]]') }}
                    </IdeoFormSelectOption>
                    <IdeoFormSelectOption v-for="option in websiteLogoOptions" :key="option.id" :value="option.id">
                        {{ option.title }}
                    </IdeoFormSelectOption>
                </IdeoFormSelect></IdeoFormGroup>
                <IdeoAction stop-propagation
                    variant="secondary"
                    icon="image"
                    :text="$t('[[[Wybierz logo]]]')"
                    @click="emit('enable-watermark'); watermarkLogoInput?.click()"
                />
            </div>
            <input ref="watermarkLogoInput" type="file" accept="image/*" class="hidden" @change="emit('watermark-logo', $event)">
            <IdeoFormGroup nospace class="mt-[12px]" :label="$t('[[[Pozycja znaku wodnego]]]')">
            <div class="grid grid-cols-2 gap-[8px]">
                <IdeoAction
                    v-for="(preset, index) in watermarkPositionPresets"
                    :key="`${preset.x}.${preset.y}`"
                    type="button"
                    :variant="isWatermarkPositionPresetActive(preset) ? 'primary' : 'secondary'"
                    :aria-pressed="isWatermarkPositionPresetActive(preset)"
                    size="xs"
                    class="w-full justify-start text-start"
                    :class="{ 'col-span-full': index == watermarkPositionPresets.length - 1 }"
                    :title="$t(preset.title)"
                    @click="emit('set-watermark-position', preset.x, preset.y)"
                >
                    <span class="inline-grid w-[16px] place-items-center font-[700] text-current">{{ preset.icon }}</span>
                    <span>{{ $t(preset.title) }}</span>
                </IdeoAction>
            </div>
            </IdeoFormGroup>
            <div class="mt-[12px] grid gap-[12px]">
                <IdeoFormGroup nospace :label="`${$t('[[[X]]]')}`" :label-for="fieldId + '-watermarkX'">
<IdeoFormNumber :id="fieldId + '-watermarkX'" suffix="%" v-model="watermarkX" :aria-label="$t('[[[Pozycja X znaku wodnego]]]')" :min="0" :max="100" :max-fraction-digits="0" @input="emit('update-watermark-control')" />
                </IdeoFormGroup>
                <IdeoFormGroup nospace :label="`${$t('[[[Y]]]')}`" :label-for="fieldId + '-watermarkY'">
<IdeoFormNumber :id="fieldId + '-watermarkY'" suffix="%" v-model="watermarkY" :aria-label="$t('[[[Pozycja Y znaku wodnego]]]')" :min="0" :max="100" :max-fraction-digits="0" @input="emit('update-watermark-control')" />
                </IdeoFormGroup>
            </div>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Przezroczystość]]]')}: ${watermarkOpacity}%`" :label-for="fieldId + '-watermarkOpacity'">
                <CropperRange :id="fieldId + '-watermarkOpacity'"
                v-model="watermarkOpacity" :aria-label="$t('[[[Przezroczystość znaku wodnego]]]')"
                :min="0"
                :max="100"
                @change="emit('update-watermark-control')"
            />
            </IdeoFormGroup>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${watermarkType === 'logo' ? $t('[[[Rozmiar logo]]]') : $t('[[[Rozmiar tekstu]]]')}: ${watermarkSize}px`" :label-for="fieldId + '-watermarkSize'">
                <CropperRange :id="fieldId + '-watermarkSize'"
                v-model="watermarkSize" :aria-label="$t('[[[Rozmiar znaku wodnego]]]')"
                :min="8"
                :max="160"
                :step="2"
                @change="emit('update-watermark-control')"
            />
            </IdeoFormGroup>
        </EditoPageSurface>

        <EditoPageSurface title="[[[Wymiary obrazu]]]">
            <IdeoFormCheckbox v-model="outputResizeEnabled" class="editor-export-toggle" @change="emit('save-history', $t('[[[Zmiana wymiarów obrazu]]]'))">
                {{ $t('[[[Ustaw własne wymiary]]]') }}
            </IdeoFormCheckbox>
            <div v-if="outputResizeEnabled" class="mt-[12px] grid gap-[12px]">
                <IdeoFormGroup nospace :label="`${$t('[[[Szerokość]]]')}`" :label-for="fieldId + '-outputWidth'">
<IdeoFormNumber :id="fieldId + '-outputWidth'" suffix="px" v-model="outputWidth" :aria-label="$t('[[[Szerokość wynikowa]]]')" :min="1" :max-fraction-digits="0" @input="emit('save-history', $t('[[[Zmiana wymiarów obrazu]]]'))" />
                </IdeoFormGroup>
                <IdeoFormGroup nospace :label="`${$t('[[[Wysokość]]]')}`" :label-for="fieldId + '-outputHeight'">
<IdeoFormNumber :id="fieldId + '-outputHeight'" suffix="px" v-model="outputHeight" :aria-label="$t('[[[Wysokość wynikowa]]]')" :min="1" :max-fraction-digits="0" @input="emit('save-history', $t('[[[Zmiana wymiarów obrazu]]]'))" />
                </IdeoFormGroup>
            </div>
        </EditoPageSurface>

        <EditoPageSurface v-if="!isLocalFileEditor" title="[[[Eksport]]]">
                        <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="$t('[[[Nazwa nowego pliku]]]')" :label-for="fieldId + '-outputFileName'"><IdeoFormInput :id="fieldId + '-outputFileName'" v-model="outputFileName" :aria-label="$t('[[[Nazwa nowego pliku]]]')" /></IdeoFormGroup>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="$t('[[[Format zapisu]]]')" :label-for="fieldId + '-exportFormat'"><IdeoFormSelect :id="fieldId + '-exportFormat'" v-model="exportFormat" :aria-label="$t('[[[Format zapisu]]]')">
                <IdeoFormSelectOption v-for="item in exportFormats" :key="item.value" :value="item.value">
                    {{ item.text }}
                </IdeoFormSelectOption>
            </IdeoFormSelect></IdeoFormGroup>
            <IdeoFormGroup nospace class="mt-[12px] first:mt-0" :label="`${$t('[[[Jakość]]]')}: ${quality}%`" :label-for="fieldId + '-quality'">
                <CropperRange :id="fieldId + '-quality'"
                v-model="quality" :aria-label="$t('[[[Jakość obrazu]]]')"
                :min="10"
                :max="100"
                :disabled="exportQualityDisabled"
            />
            </IdeoFormGroup>
            <IdeoFormCheckbox v-model="optimizeDimensions" class="editor-export-toggle mt-[8px]" @change="emit('save-history', $t('[[[Zmiana optymalizacji wymiarów]]]'))">
                {{ $t('[[[Zmniejsz duże obrazy do WWW]]]') }}
            </IdeoFormCheckbox>
        </EditoPageSurface>

    </div>
</template>
